// oxlint-disable react/only-export-components
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

const pillBadgeVariants = cva(
  "group/badge inline-flex shrink-0 items-center justify-center rounded-full font-semibold whitespace-nowrap [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      layout: {
        character: "",
        "character-dot": "gap-1 bg-neutrals-v2-200 text-labels-primary",
        icon: "size-6 bg-brand-red-200 p-0 text-neutrals-v2-100",
      },
      size: {
        xs: "min-w-5 gap-1 px-1 py-0.5 text-xs leading-4",
        sm: "min-w-6 gap-1 px-2 py-1 text-xs leading-4",
        md: "gap-1 px-3 py-1.5 text-sm leading-5",
      },
    },
    compoundVariants: [
      {
        layout: "character",
        size: ["xs", "sm"],
        class: "bg-brand-red-200 text-neutrals-v2-100",
      },
      {
        layout: "character",
        size: "md",
        class: "bg-neutrals-v2-200 text-labels-primary",
      },
      { layout: "character-dot", size: "md", class: "gap-2" },
      // Der Icon-Layout hat im Design eine fixe 24x24-Box ohne Innenabstand —
      // die Größen-Paddings der `size`-Achse greifen hier nicht.
      { layout: "icon", class: "min-w-0 gap-0 px-0 py-0" },
    ],
    defaultVariants: {
      layout: "character",
      size: "sm",
    },
  }
)

const rectangleBadgeVariants = cva(
  "group/badge inline-flex shrink-0 items-center justify-center gap-1 rounded-md px-1.5 font-semibold whitespace-nowrap [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-neutrals-v2-200 py-1 text-xs leading-4 text-labels-primary hover:bg-neutrals-v2-300",
        blue: "bg-neutrals-v2-200 py-0.5 text-sm leading-5 text-indigo-100 hover:bg-neutrals-v2-300",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export type BadgePillLayout = NonNullable<
  VariantProps<typeof pillBadgeVariants>["layout"]
>

export type BadgePillSize = NonNullable<
  VariantProps<typeof pillBadgeVariants>["size"]
>

export type BadgeRectangleVariant = NonNullable<
  VariantProps<typeof rectangleBadgeVariants>["variant"]
>

type BadgeCommonProps = ComponentProps<"span"> & {
  asChild?: boolean
}

export type BadgePillProps = BadgeCommonProps & {
  shape?: "pill"
  /** Figma-Achse „Layout": Zahl/Text, Zahl/Text mit Farbpunkt, oder reines Icon. */
  layout?: BadgePillLayout
  size?: BadgePillSize
  /** Nur bei `layout="character"` (optional) und `layout="icon"` (erforderlich) sichtbar. */
  icon?: ReactNode
  /** Farbe des Indikator-Punkts als Hex-Wert (z. B. `"#2874ff"`). Nur bei `layout="character-dot"`. */
  dotColor?: string
}

export type BadgeRectangleProps = BadgeCommonProps & {
  shape: "rectangle"
  variant?: BadgeRectangleVariant
  icon?: ReactNode
  rightIcon?: ReactNode
  showLeftIcon?: boolean
  showRightIcon?: boolean
}

export type BadgeProps = BadgePillProps | BadgeRectangleProps

// Flex-Container + `block`-SVG verhindert, dass das Icon als Inline-Element an
// der Text-Baseline ausgerichtet wird (FontAwesome setzt zusätzlich ein
// `vertical-align`) und dadurch vertikal verschoben erscheint.
const badgeIconClassName =
  "flex shrink-0 items-center justify-center [&>svg]:block [&>svg]:size-full"

const DEFAULT_DOT_COLOR = "var(--color-brand-red-200)"

function Badge(props: BadgeProps) {
  const { className, asChild = false, children, ...rest } = props
  const Comp = asChild ? Slot.Root : "span"

  if (rest.shape === "rectangle") {
    const {
      // oxlint-disable-next-line no-unused-vars
      shape,
      variant = "default",
      icon,
      rightIcon,
      showLeftIcon = true,
      showRightIcon = false,
      ...spanProps
    } = rest

    return (
      <Comp
        data-slot="badge"
        data-shape="rectangle"
        data-variant={variant}
        className={cn(rectangleBadgeVariants({ variant, className }))}
        {...spanProps}
      >
        {showLeftIcon && icon && (
          <span
            data-slot="badge-icon"
            className={cn(badgeIconClassName, "size-3.5")}
          >
            {icon}
          </span>
        )}
        <Slot.Slottable>{children}</Slot.Slottable>
        {showRightIcon && rightIcon && (
          <span
            data-slot="badge-icon"
            className={cn(badgeIconClassName, "size-3.5")}
          >
            {rightIcon}
          </span>
        )}
      </Comp>
    )
  }

  const {
    // oxlint-disable-next-line no-unused-vars
    shape,
    layout = "character",
    size = "sm",
    icon,
    dotColor = DEFAULT_DOT_COLOR,
    ...spanProps
  } = rest

  return (
    <Comp
      data-slot="badge"
      data-shape="pill"
      data-layout={layout}
      data-size={size}
      className={cn(pillBadgeVariants({ layout, size, className }))}
      {...spanProps}
    >
      {layout === "character" && icon && (
        <span
          data-slot="badge-icon"
          className={cn(
            badgeIconClassName,
            size === "md" ? "size-[18px]" : "size-3.5"
          )}
        >
          {icon}
        </span>
      )}
      {layout === "character-dot" && (
        <span
          data-slot="badge-dot"
          className={cn(
            "shrink-0 rounded-full",
            size === "md" ? "size-2.5" : "size-2"
          )}
          style={{ backgroundColor: dotColor }}
        />
      )}
      {layout === "icon" ? (
        <span
          data-slot="badge-icon"
          className={cn(badgeIconClassName, "size-3.5")}
        >
          {icon}
        </span>
      ) : (
        <Slot.Slottable>{children}</Slot.Slottable>
      )}
    </Comp>
  )
}

export { Badge, pillBadgeVariants, rectangleBadgeVariants }
